import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { applyUnsubscribe } from "@/lib/unsubscribe.functions";

const searchSchema = z.object({
  token: z.string().optional(),
  category: z.enum(["product_updates", "reengagement"]).optional(),
});

export const Route = createFileRoute("/unsubscribe")({
  head: () => ({
    meta: [
      { title: "Unsubscribe — CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  validateSearch: searchSchema,
  component: UnsubscribePage,
});

const CATEGORY_LABEL: Record<string, string> = {
  product_updates: "Product updates (low-balance and depletion notices)",
  reengagement: "Reengagement (abandoned checkout and inactivity nudges)",
};

function UnsubscribePage() {
  const { token, category } = Route.useSearch();
  const apply = useServerFn(applyUnsubscribe);
  const mutation = useMutation({
    mutationFn: () => apply({ data: { token: token!, category: category! } }),
  });

  const ready = !!token && !!category;

  return (
    <main className="mx-auto max-w-[560px] px-4 pb-16 pt-12">
      <h1 className="font-serif text-[28px] text-brand-ink">Manage your subscription</h1>
      {!ready && (
        <p className="mt-4 text-[14px] text-brand-muted">
          This link is missing or invalid. Sign in and open Profile → Email to
          manage your preferences.
        </p>
      )}
      {ready && mutation.isIdle && (
        <>
          <p className="mt-4 text-[14px] text-brand-ink">
            Unsubscribe from <strong>{CATEGORY_LABEL[category!] ?? category}</strong>?
          </p>
          <p className="mt-2 text-[12.5px] text-brand-muted">
            Transactional emails (run notifications, receipts, account changes)
            cannot be disabled.
          </p>
          <div className="mt-6 flex gap-3">
            <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
              {mutation.isPending ? "Updating…" : "Unsubscribe"}
            </Button>
          </div>
        </>
      )}
      {mutation.isSuccess && (
        <p className="mt-6 text-[14px] text-brand-ink">
          You have been unsubscribed from {CATEGORY_LABEL[category!] ?? category}.
          You can re-enable it any time from Profile → Email.
        </p>
      )}
      {mutation.isError && (
        <p className="mt-6 text-[14px] text-destructive">
          We could not process this request: {(mutation.error as Error).message}
        </p>
      )}
    </main>
  );
}